import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase, Profile, AnimeCharacter } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/SEO';

export default function Top5UsersPage() {
  const [users, setUsers] = useState<Profile[]>([]);
  const [randomCharacters, setRandomCharacters] = useState<AnimeCharacter[]>([]);
  const [featuredCharacter, setFeaturedCharacter] = useState<AnimeCharacter | null>(null);
  const [loading, setLoading] = useState(true);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    loadTopUsers();
    loadRandomCharacters();
    loadFeaturedCharacter();
  }, []);

  const loadTopUsers = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .order('ratings_count', { ascending: false })
      .limit(5);

    if (data) {
      setUsers(data);
    }
    setLoading(false);
  };

  const loadRandomCharacters = async () => {
    try {
      // Get total count of characters
      const { count } = await supabase
        .from('anime_characters')
        .select('*', { count: 'exact', head: true });

      if (!count || count === 0) return;

      // Generate 6 random offsets
      const randomOffsets = Array.from({ length: 6 }, () => 
        Math.floor(Math.random() * count)
      );

      // Fetch characters at random positions
      const characterPromises = randomOffsets.map(offset =>
        supabase
          .from('anime_characters')
          .select('*')
          .range(offset, offset)
          .single()
      );

      const results = await Promise.all(characterPromises);
      const characters = results
        .filter(result => result.data)
        .map(result => result.data);

      setRandomCharacters(characters);
    } catch (err) {
      console.error('Error loading random characters:', err);
    }
  };

  const loadFeaturedCharacter = async () => {
    try {
      // Get total count of characters with descriptions
      const { count } = await supabase
        .from('anime_characters')
        .select('*', { count: 'exact', head: true })
        .not('description', 'is', null)
        .neq('description', '');

      if (!count || count === 0) return;

      // Generate a random offset
      const randomOffset = Math.floor(Math.random() * count);

      // Fetch character at random position
      const { data } = await supabase
        .from('anime_characters')
        .select('*')
        .not('description', 'is', null)
        .neq('description', '')
        .range(randomOffset, randomOffset)
        .single();

      if (data) {
        setFeaturedCharacter(data);
      }
    } catch (err) {
      console.error('Error loading featured character:', err);
    }
  };

  const getAvatarStyle = (profile: Profile, isMobile: boolean = false) => {
    if (!profile?.avatar_zoom) {
      return {
        objectFit: 'cover' as const,
        objectPosition: 'top center',
      };
    }

    const zoom = profile.avatar_zoom;
    // Use 48px for mobile (sm:w-16 means w-12 on mobile), 64px for desktop
    const containerSize = isMobile ? 48 : 64;
    
    // The cropped area dimensions from react-easy-crop
    const cropWidth = zoom.width;
    const cropHeight = zoom.height;
    
    // Calculate how much to scale the image so the cropped area fits the container
    const scale = containerSize / cropWidth;
    
    // Calculate the offset needed to position the cropped area in the center
    const offsetX = -zoom.x * scale;
    const offsetY = -zoom.y * scale;

    return {
      transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
      transformOrigin: 'top left',
      width: 'auto',
      height: 'auto',
      maxWidth: 'none',
    };
  };

  // Helper function to create character URL slug
  const createCharacterSlug = (char: AnimeCharacter) => {
    const animePart = char.anime_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const namePart = char.character_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return `${animePart}-${namePart}-${char.id}`;
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Top 5 Anime Raters',
    description: 'The most active members of our anime rating community',
    numberOfItems: users.length,
    itemListElement: users.map((user, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Person',
        name: user.username,
        image: user.avatar_url || undefined,
        url: `${siteUrl}/profile/${user.id}`,
        interactionStatistic: {
          '@type': 'InteractionCounter',
          interactionType: 'https://schema.org/ReviewAction',
          userInteractionCount: user.ratings_count
        }
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <SEO
        title="Top 5 Raters - Most Active Community Members"
        description="Meet the top 5 most active members of our anime rating community. See who has submitted the most character ratings and join the leaderboard!"
        keywords="top anime raters, anime community members, active anime fans, anime rating leaderboard, top contributors"
        canonical={`${siteUrl}/top5users`}
        jsonLd={jsonLd}
      />
      
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
              Top 5 Raters
            </h1>
            <p className="text-sm text-gray-600">
              The most active members of our community
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent"></div>
            </div>
          ) : users.length > 0 ? (
            <>
              <div className="space-y-3 sm:space-y-4">
                {users.map((user, index) => (
                  <Link
                    key={user.id}
                    to={`/profile/${user.id}`}
                    className="block bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden"
                  >
                    <div className="flex items-center gap-3 sm:gap-6 p-4 sm:p-6">
                      <div className="flex-shrink-0">
                        <div
                          className={`w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center rounded-full text-lg sm:text-2xl font-bold ${
                            index === 0
                              ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white'
                              : index === 1
                              ? 'bg-gradient-to-r from-gray-300 to-gray-500 text-white'
                              : index === 2
                              ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white'
                              : 'bg-gradient-to-r from-pink-100 to-purple-100 text-gray-700'
                          }`}
                        >
                          {index + 1}
                        </div>
                      </div>

                      <div className="flex-shrink-0">
                        {user.avatar_url ? (
                          <>
                            {/* Mobile avatar */}
                            <div className="w-12 h-12 sm:hidden rounded-full overflow-hidden border-2 border-pink-500 bg-gray-100">
                              <img
                                src={user.avatar_url}
                                alt={user.username}
                                className="w-full h-full object-cover"
                                style={getAvatarStyle(user, true)}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </div>
                            {/* Desktop avatar */}
                            <div className="hidden sm:block w-16 h-16 rounded-full overflow-hidden border-2 sm:border-3 border-pink-500 bg-gray-100">
                              <img
                                src={user.avatar_url}
                                alt={user.username}
                                className="w-full h-full object-cover"
                                style={getAvatarStyle(user, false)}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </div>
                          </>
                        ) : (
                          <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 rounded-full text-white text-lg sm:text-2xl font-bold">
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2 truncate">
                          {user.username}
                        </h3>
                        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600">
                          <i className="ri-heart-fill text-pink-500"></i>
                          <span>
                            <strong>{user.ratings_count}</strong> ratings
                          </span>
                        </div>
                      </div>

                      <div className="flex-shrink-0 hidden sm:block">
                        <i className="ri-arrow-right-line text-2xl text-gray-400"></i>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* More Characters Section */}
              {randomCharacters.length > 0 && (
                <div className="bg-white rounded-2xl shadow-xl p-6 mt-8 sm:mt-12">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">More Characters</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                    {randomCharacters.map((char) => (
                      <Link
                        key={char.id}
                        to={`/character/${createCharacterSlug(char)}`}
                        className="group cursor-pointer"
                      >
                        <div className="aspect-[3/4] rounded-lg overflow-hidden mb-2 bg-gray-100">
                          <img
                            src={char.image_url}
                            alt={char.character_name}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <h3 className="text-xs font-semibold text-gray-900 line-clamp-2 group-hover:text-pink-500 transition-colors">
                          {char.character_name}
                        </h3>
                        <div className="flex items-center gap-1 mt-1">
                          <i className="ri-heart-fill text-xs text-pink-500"></i>
                          <span className="text-xs text-gray-600">{char.average_rating.toFixed(1)}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Featured Character Section */}
              {featuredCharacter && (
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden mt-8 sm:mt-12">
                  <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Featured Character</h2>
                  </div>
                  <Link
                    to={`/character/${createCharacterSlug(featuredCharacter)}`}
                    className="block hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row gap-6 p-6 pt-0">
                      <div className="flex-shrink-0 w-full sm:w-48">
                        <div className="aspect-[3/4] rounded-lg overflow-hidden bg-gray-100">
                          <img
                            src={featuredCharacter.image_url}
                            alt={featuredCharacter.character_name}
                            className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          {featuredCharacter.character_name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                          From <span className="font-semibold">{featuredCharacter.anime_name}</span>
                        </p>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="flex items-center gap-1">
                            <i className="ri-heart-fill text-pink-500"></i>
                            <span className="text-sm font-semibold text-gray-900">
                              {featuredCharacter.average_rating.toFixed(1)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {featuredCharacter.total_ratings} ratings
                          </div>
                        </div>
                        {featuredCharacter.description && (
                          <div 
                            className="text-sm text-gray-700 leading-relaxed character-description-content"
                            dangerouslySetInnerHTML={{ __html: featuredCharacter.description }}
                          />
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-base sm:text-lg text-gray-600">No users have submitted ratings yet.</p>
            </div>
          )}
        </div>
      </div>

      <Footer />
      <style>{`
        .character-description-content p {
          margin-bottom: 1.5em;
        }
        .character-description-content p:last-child {
          margin-bottom: 0;
        }
        .character-description-content ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        .character-description-content ul li {
          margin-bottom: 0.5rem;
        }
        .character-description-content ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        .character-description-content ol li {
          margin-bottom: 0.5rem;
        }
      `}</style>
    </div>
  );
}
